/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import Alert from "../Others/Alert";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { inputClass } from "@/lib/formStyles";

const formatNumber = (value = 0) =>
  new Intl.NumberFormat("fr-FR").format(value || 0);

const TabOne = ({
  form,
  handleChange,
  handleSubmit,
  handleUpdate,
  drugs,
  handleDelete,
  errors,
  drugSearch,
  setDrugSearch,
  drugsPagination,
  onDrugPageChange,
  drugsFetching,
}) => {
  const {
    totalElements = 0,
    totalPages = 0,
    currentPage = 0,
    hasNext = false,
    hasPrevious = false,
  } = drugsPagination || {};

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-100 rounded-[28px] border border-slate-200 bg-white shadow-sm p-6 md:p-8">
        <div className="space-y-3">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
            <i className="bx bx-capsule text-base"></i>
            Inventaire pharmacie
          </span>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
            Médicaments
          </h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Référencez les médicaments du stock, ajustez le prix de base et
            suivez les unités disponibles.
          </p>
        </div>

        {errors?.length > 0 && (
          <div className="mt-6 space-y-2">
            {errors.map((error, idx) => (
              <Alert key={idx} message={error.message} />
            ))}
          </div>
        )}

        <form
          id="createFormDrugs"
          className="mt-8 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_auto_auto] gap-3 items-end"
        >
          <label className="block">
            <span className="block text-sm font-medium text-slate-600 mb-1">
              Médicament
            </span>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex. Paracétamol 500mg"
              className={cn(inputClass, "mt-0")}
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-slate-600 mb-1">
              Stock initial
            </span>
            <input
              type="number"
              name="stock"
              min="1"
              value={form.stock}
              onChange={handleChange}
              className={cn(inputClass, "mt-0")}
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-slate-600 mb-1">
              Prix de base
            </span>
            <input
              type="number"
              name="basePrice"
              min="1"
              value={form.basePrice}
              onChange={handleChange}
              className={cn(inputClass, "mt-0")}
            />
          </label>
          <button
            type="button"
            onClick={handleSubmit}
            className="group/cta relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-green-600 px-5 h-11 text-sm font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 active:bg-green-800"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover/cta:translate-x-full" />
            <i className="bx bx-plus relative z-10 text-base"></i>
            <span className="relative z-10">
              {form.action === "edit" ? "Mettre à jour" : "Ajouter"}
            </span>
          </button>
          <button
            type="button"
            onClick={(e) => handleUpdate(e, null)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 h-11 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <i className="bx bx-eraser text-base"></i>
            Effacer
          </button>
        </form>
      </section>

      {/* Search — serveur, debounce 300 ms côté parent */}
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-200">
        <div className="relative w-full max-w-md">
          <i className="bx bx-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none"></i>
          <input
            type="text"
            value={drugSearch}
            onChange={(e) => setDrugSearch(e.target.value)}
            placeholder="Rechercher un médicament…"
            className={cn(inputClass, "mt-0 pl-11")}
          />
        </div>
      </section>

      {/* Table */}
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-300 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <th className="px-4 py-3 text-left">Médicament</th>
                <th className="px-4 py-3 text-right">Prix de base</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3 text-right pr-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drugs.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 mb-3">
                        <i className="bx bx-capsule text-2xl"></i>
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        Aucun médicament référencé
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Renseignez le formulaire ci-dessus pour ajouter votre
                        premier médicament.
                      </p>
                    </div>
                  </td>
                </tr>
              )}

              {drugs.map((drug, idx) => {
                const lowStock = (drug?.stock || 0) <= 5;
                return (
                  <tr
                    key={drug?.id ?? idx}
                    className="group/row border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-2 ring-white">
                          <i className="bx bx-capsule text-base"></i>
                        </div>
                        <div className="text-sm font-medium text-slate-800">
                          {drug?.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-slate-800">
                      {formatNumber(drug?.basePrice)}{" "}
                      <span className="text-xs text-slate-400 font-normal">
                        FCFA
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
                          lowStock
                            ? "border-rose-100 bg-rose-50 text-rose-600"
                            : "border-emerald-100 bg-emerald-50 text-emerald-700"
                        )}
                      >
                        {lowStock && (
                          <i className="bx bx-error-circle text-xs"></i>
                        )}
                        {drug?.stock || 0} unités
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity pr-2">
                        <button
                          type="button"
                          title="Modifier"
                          onClick={(e) => handleUpdate(e, drug)}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                        >
                          <i className="bx bx-edit-alt text-base"></i>
                        </button>
                        <button
                          type="button"
                          title="Supprimer"
                          onClick={(e) => handleDelete(e, drug)}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                        >
                          <i className="bx bx-trash text-base"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        currentCount={drugs.length}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        onPageChange={onDrugPageChange}
        itemLabel="médicament"
        isLoading={drugsFetching}
      />
    </div>
  );
};

export default TabOne;
